/**
 * 10.9CC — ARENA DO GUARDIÃO (boss de área).
 *
 * Antes o "boss de área" nascia por TIMER (1h) em cima do player, em qualquer lugar — Thiago:
 * "essa mecânica não tá boa". Agora cada região tem UMA arena fixa (num território); abater
 * monstros na região CARREGA o guardião (contador visível no rótulo); cheio → ele desperta e
 * ESPERA na arena (marcador no mapa). O boss só nasce quando o player entra e aceita o desafio.
 *
 * Só visual + geometria (anel, pilares, runa, rótulo). Quem conta abates e spawna é o Game.
 */
import * as THREE from "three";
import { disposeLabel, makeLabel } from "./StructureLabel";
import { crystalStdMat, energyMat, stoneMat } from "./Materials";

const DIM = 0x7c4dff;   // carregando
const READY = 0xffb347; // desperto (dourado quente)

export class GuardianArena {
  readonly group = new THREE.Group();
  private readonly ringMat: THREE.MeshBasicMaterial;
  private readonly runeMat: THREE.MeshBasicMaterial;
  private readonly crystalMat: THREE.MeshStandardMaterial;
  private readonly crystals: THREE.Mesh[] = [];
  private label: THREE.Sprite | null = null;
  private labelKey = "";
  private ready = false;
  private time = Math.random() * 10;

  constructor(
    private readonly scene: THREE.Scene,
    readonly cx: number,
    readonly cz: number,
    readonly radius: number,
  ) {
    const r = radius;
    // anel de energia no chão (clone: pulsa e troca de cor)
    this.ringMat = energyMat(DIM, "ring").clone();
    const ring = new THREE.Mesh(new THREE.RingGeometry(r - 0.55, r, 72), this.ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.06;
    this.group.add(ring);
    // runa central (hexágono) — onde o guardião nasce
    this.runeMat = energyMat(DIM, "disc").clone();
    const rune = new THREE.Mesh(new THREE.RingGeometry(1.6, 2.6, 6), this.runeMat);
    rune.rotation.x = -Math.PI / 2;
    rune.position.y = 0.07;
    this.group.add(rune);
    // 8 pilares de pedra na borda com cristal no topo
    this.crystalMat = crystalStdMat(DIM).clone();
    const pillarGeo = new THREE.CylinderGeometry(0.34, 0.5, 3.2, 6);
    const crystalGeo = new THREE.OctahedronGeometry(0.42, 0);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const px = Math.sin(a) * (r + 0.9), pz = Math.cos(a) * (r + 0.9);
      const pillar = new THREE.Mesh(pillarGeo, stoneMat(0x2e2a44));
      pillar.position.set(px, 1.6, pz);
      pillar.castShadow = true;
      this.group.add(pillar);
      const c = new THREE.Mesh(crystalGeo, this.crystalMat);
      c.position.set(px, 3.5, pz);
      this.crystals.push(c);
      this.group.add(c);
    }
    this.group.position.set(cx, 0, cz);
    scene.add(this.group);
  }

  /** Atualiza rótulo/cores: `kills/need` carregando, ou DESPERTO quando pronto. */
  setProgress(text: string, ready: boolean, color: string): void {
    this.ready = ready;
    const c = ready ? READY : DIM;
    this.ringMat.color.setHex(c);
    this.runeMat.color.setHex(c);
    this.crystalMat.color.setHex(c);
    this.crystalMat.emissive.setHex(c);
    const key = `${text}|${color}`;
    if (key === this.labelKey) return;
    this.labelKey = key;
    if (this.label) disposeLabel(this.label);
    this.label = makeLabel(text, color, 5.2);
    this.group.add(this.label);
  }

  isPlayerInside(p: THREE.Vector3): boolean {
    const dx = p.x - this.cx, dz = p.z - this.cz;
    return dx * dx + dz * dz <= this.radius * this.radius;
  }

  update(dt: number): void {
    this.time += dt;
    const k = this.ready ? 0.55 + Math.sin(this.time * 4) * 0.3 : 0.28 + Math.sin(this.time * 1.4) * 0.1;
    this.ringMat.opacity = k;
    this.runeMat.opacity = k * 0.9;
    this.crystalMat.emissiveIntensity = this.ready ? 1.0 + Math.sin(this.time * 5) * 0.2 : 0.6;
    for (let i = 0; i < this.crystals.length; i++) {
      const c = this.crystals[i];
      c.rotation.y += dt * (this.ready ? 2.2 : 0.6);
      c.position.y = 3.5 + Math.sin(this.time * 2 + i) * 0.12;
    }
  }

  dispose(): void {
    if (this.label) disposeLabel(this.label);
    this.scene.remove(this.group);
    this.group.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
    this.ringMat.dispose();
    this.runeMat.dispose();
    this.crystalMat.dispose();
  }
}
